import { Box, type SxProps, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Suspense } from 'react';
import { useProduct } from 'themes/ProductThemeProvider';
import type { TProductName } from 'themes/tokens/productTheme';
import IconSurface from './IconSurface';
import { resolveIcon, type TIconName } from './registry';

/**
 * The second ink slot. Duotone exports draw the glyph in the brand ink and
 * strike it through with this coral, so the two must not collapse to one
 * colour — the accent follows `secondary.main` while the glyph follows
 * `color`.
 */
const INK_ACCENT = '#FF6F61';

/**
 * Paint values a recolour leaves alone:
 *
 *  - `none` is structural — it keeps stroked shapes hollow;
 *  - white is a knockout halo separating the accent slash from the ink it
 *    crosses, so recolouring it would weld the two together;
 *  - the accent has its own rule below.
 */
const RESERVED = ['none', 'white', '#fff', '#ffffff', INK_ACCENT];

/** `:not()` chain excluding every RESERVED value of one paint attribute. */
const exceptReserved = (attr: string) =>
  RESERVED.map((value) => `:not([${attr}="${value}" i])`).join('');

export type TIconProps = {
  name: TIconName;
  /** Square size in px. Default 24. */
  size?: number;
  /**
   * The icon's colour. Takes any of the forms MUI's `sx` accepts:
   *
   *   color="primary.main"                      // theme path
   *   color="text.secondary"
   *   color="#4389E1"                           // literal
   *   color={(theme) => theme.palette.error.main}  // callback
   *
   * Defaults to the surrounding product's colour, so an icon inherits the page
   * it sits on with no prop at all. Has no effect when `monochrome` is off —
   * nothing consumes `currentColor` then.
   */
  color?: string | ((theme: Theme) => string);
  /** Force a product instead of the one the surrounding subtree is themed to. */
  product?: TProductName;
  /**
   * Recolour every fill and stroke in the SVG to `color`. On by default —
   * everything in the registry is there because it is recolourable.
   *
   * Turn it off for product art that needs the registry's per-product
   * resolution but keeps its own palette (a gradient or two-tone mark). An
   * icon that is NEVER recoloured does not belong in the registry at all: it
   * gains nothing from inlining and should stay an `<img>`.
   */
  monochrome?: boolean;
  /**
   * Repaint the SVG's gradient stops from the palette: the first stop of each
   * `<linearGradient>` takes `primary.main`, the last takes `primary.dark`.
   *
   * For product art that is gradient-filled, where `monochrome` is not an
   * option — rewriting a `fill="url(#…)"` to `currentColor` would flatten the
   * gradient away entirely. The home plan-list header needs this because its
   * stops are baked to instaview's blue and would stay instaview-blue on every
   * other partner.
   */
  themeGradient?: boolean;
  /**
   * Wrap the icon in a circular surface — Figma "Icon Buttons" (50x50, r:999).
   * Off by default: most icons sit inline in a row of text or a list, where a
   * disc would be wrong. Opt in where the design shows one.
   *
   * `solid` is Figma’s raised disc — the paper surface with a single soft
   * offset shadow (the "Round Button" behind a product mark). `flat` is the
   * same surface with no shadow, which is what the "Plan Benefits Card" rows
   * use. `glass` is the Liquid Glass disc, for placing an icon on a photo,
   * busy backdrop, or an unselected glass card. `glassTinted` is the same
   * disc at the "active glass" weight — a translucent product-colour fill —
   * for the same "this one's selected" states the plan cards and toggle use
   * it for (the featured/Prime plan's icon). Both also add a faint glow
   * behind the glyph, so it reads as floating on the glass rather than
   * printed on it.
   */
  surface?: 'glass' | 'glassTinted' | 'solid' | 'flat';
  /** Diameter of the `surface` disc in px. Default 50, per Figma. */
  surfaceSize?: number;
  sx?: SxProps<Theme>;
  title?: string;
  onClick?: () => void;
};

/**
 * A themeable SVG icon.
 *
 * The colour is applied in CSS rather than baked into the file, so ONE icon
 * asset serves every product. That only works because the SVG is inlined into
 * the document (vite-plugin-svgr) — an `<img src="…svg">` renders in its own
 * isolated document that page CSS cannot reach into, which is why the CDN
 * icons cannot be themed no matter where they are hosted.
 *
 * The circular surface is opt-in via `surface` — an icon is bare by default,
 * because most sit inline in text or a list where a disc would be wrong.
 *
 * `monochrome` rewrites the SVG's own hardcoded fills/strokes at RUNTIME
 * instead of at build time. That keeps the asset files untouched, leaves every
 * existing `?react` import behaving exactly as it does today, and makes the
 * behaviour opt-in per usage rather than global.
 *
 * @example
 *   <Icon name="videoCamera" />                    // bare, product colour
 *   <Icon name="info" color="text.secondary" />    // explicit colour
 *   <Icon name="eye" surface="glass" />            // in the glass disc
 *   <Icon name="cardVisa" />                       // keeps its own palette
 */
function Icon(props: TIconProps) {
  const {
    name,
    size = 24,
    color,
    product,
    monochrome,
    themeGradient,
    surface,
    surfaceSize = 50,
    sx,
    title,
    onClick
  } = props;

  const recolour = monochrome ?? true;

  const activeProduct = useProduct();
  const Svg = resolveIcon(name, product ?? activeProduct);

  if (!Svg) return null;

  const icon = (
    <Box
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}
      sx={[
        (theme: Theme) => {
          // `color` drives currentColor below; default to the active product.
          const resolvedColor =
            (typeof color === 'function' ? color(theme) : color) ??
            (recolour ? theme.palette.primary.main : undefined);
          // The glow (glass surfaces only, below) needs a real hex/rgb to
          // pass through `alpha()` — `resolvedColor` can also be a bare MUI
          // theme-path string (`color="text.secondary"`), which `alpha()`
          // can't resolve on its own, so that form skips the glow rather
          // than risk a broken `filter` value.
          const isThemePathString =
            typeof color === 'string' && !/^#|^rgb/.test(color);
          const glowColor = isThemePathString ? undefined : resolvedColor;
          const isGlass = surface === 'glass' || surface === 'glassTinted';

          return {
            display: 'inline-flex',
            flexShrink: 0,
            width: size,
            height: size,
            color: resolvedColor,
            // A faint glow so the glyph reads as floating on the glass disc
            // behind it, not printed flat on top of it.
            ...(isGlass &&
              glowColor && {
                filter: `drop-shadow(0 0 4px ${alpha(glowColor, 0.4)})`
              }),
            '& > svg': { width: '100%', height: '100%', display: 'block' },
            ...(recolour && {
              [`& [fill]${exceptReserved('fill')}`]: { fill: 'currentColor' },
              [`& [stroke]${exceptReserved('stroke')}`]: {
                stroke: 'currentColor'
              },
              // The accent is a colour slot too, just a different one — a
              // single-ink icon simply has no element matching these.
              [`& [fill="${INK_ACCENT}" i]`]: {
                fill: theme.palette.secondary.main
              },
              [`& [stroke="${INK_ACCENT}" i]`]: {
                stroke: theme.palette.secondary.main
              }
            }),
            ...(themeGradient && {
              '& linearGradient stop:first-of-type': {
                stopColor: theme.palette.primary.main
              },
              '& linearGradient stop:last-of-type': {
                stopColor: theme.palette.primary.dark
              }
            })
          };
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      {/* Feature icons are lazy (see featureIcons.ts); the box above already
          reserves the size, so no fallback is needed. Sync icons never suspend. */}
      <Suspense fallback={null}>
        <Svg />
      </Suspense>
    </Box>
  );

  if (!surface) return icon;

  return (
    <IconSurface variant={surface} size={surfaceSize} onClick={onClick}>
      {icon}
    </IconSurface>
  );
}

export default Icon;
